import async from 'async';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';

import { default as Button, default as IconButton } from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';

//- local
import DeliveryEstimate from '../../components/DeliveryEstimate';
import ConfirmCancelDeliveryDialog from '../../components/Dialogs/ConfirmCancelDeliveryDialog';
import ConfirmUncancelDeliveryDialog from '../../components/Dialogs/ConfirmUncancelDeliveryDialog';
import ConfirmLockedDeliveryEditDialog from '../../components/Dialogs/ConfirmLockedDeliveryEditDialog';
import LockedDeliveryAlert from '../../components/LockedDeliveryAlert';
import ConfirmDeliveryDialog from '../../components/Dialogs/ConfirmDeliveryDialog';
import UpdatingDeliveryDialog from '../../components/Dialogs/UpdatingDeliveryDialog';
import CarrierSelect from '../../components/form/CarrierSelect';
import ResourceUploader from '../../components/form/ResourceUploader';
import TerminalSupplierPicker from '../../components/pureForm/DeliveryTerminalSupplierPicker';
import FMPTimePicker from '../../components/pureForm/FMPTimePicker';
import FMPTextField from '../../components/ui/FMPTextField';
import AuthUserContainer from '../../containers/AuthUser';
import CarriersContainer from '../../containers/logistics/Carriers';
import LoadingAssignmentsContainer from '../../containers/logistics/LoadingAssignments';
import DeliveryContainer from '../../containers/logistics/StationDelivery';
import TerminalsContainer from '../../containers/logistics/Terminals';
import StationsContainer from '../../containers/Stations';

//- Pureform
import FMPDatePicker from '../../components/pureForm/FMPDatePicker';
import FMPProductSelect from '../../components/pureForm/FMPProductSelect';
import FMPSelect from '../../components/pureForm/FMPSelect';
import StationTankSelect from '../../components/pureForm/StationTankSelect';
import FMPRegionHeader from '../../components/ui/FMPRegionHeader';
import Note from '../../components/ui/Note';
import WarningIcon from '@material-ui/icons/Warning';

//- lib
import { valueToDisplay } from '../../libs/products';
import { getLegs as getDeliveryLegs, loadingNumberField } from '../../libs/deliveryLegs';
import * as deliveryLockLib from '../../libs/deliveryLock';

//- Stops past the first, in order (max 4 stops per delivery)
const EXTRA_LEG_PREFIXES = ['secondary', 'tertiary', 'quaternary'];
const upperFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const styles = (theme) => {
	const primaryColor = theme.palette.primary[500];
	return {
		sectionHeader: {
			margin: '30px 0px 10px 0px',
			textAlign: 'left',
			textTransform: 'uppercase',
			fontSize: 14,
			color: '#777',
			fontWeight: 900,
			letterSpacing: 2,
		},
		allDayContainer: {
			margin: '15px 0px 0px 0px',
			letterSpacing: 0,
		},
		sectionDivider: {
			backgroundColor: primaryColor,
		},
		paper: {
			width: '100%',
			overflowX: 'auto',
			textAlign: 'left',
			boxSizing: 'border-box',
			padding: 25,
			marginTop: 20,
			[theme.breakpoints.up('md')]: {
				padding: '10px 40px 40px 40px',
			},
		},
		addInputButton: {
			borderRadius: '100px',
			marginTop: 20,
			marginBottom: 30,
			textTransform: 'uppercase',
		},
		submitButton: {
			fontSize: 18,
			borderRadius: '100px',
			padding: '20px 30px',
			textTransform: 'uppercase',
		},
		formControl: {
			width: '100%',
			margin: '10px 0',
		},
		splitLoadSection: {
			transition: 'max-height .5s ease-in-out',
			overflow: 'hidden',
			maxHeight: 0,
		},
		terminalGroup: {
			borderLeft: '5px solid #ccc',
			marginTop: 10,
			marginBottom: 20,
			paddingLeft: 10,
			paddingTop: 10,
		},
		terminalSection: {
			paddingBottom: 30,
		},
		pickup: {
			margin: '15px 0px',
		},
		allTechAlert: {
			fontSize: 18,
			padding: 20,
			border: '2px solid #f44336',
			margin: 20,
			backgroundColor: 'white',
		},
		cancelledAlert: {
			fontSize: 18,
			fontWeight: 600,
			padding: 20,
			border: '2px solid #f44336',
			marginBottom: 20,
			backgroundColor: '#ffebee',
			color: '#b71c1c',
		},
		formFieldset: {
			border: 'none',
			margin: 0,
			padding: 0,
			minWidth: 0,
		},
		lockedForm: {
			pointerEvents: 'none',
			opacity: 0.6,
		},
		pickFuelType: {
			padding: 20,
			fontSize: 20,
			margin: '20px 10px',
			border: '1px solid #ddd',
			backgroundColor: '#f5f5f5',
			color: '#666',
		},
		errorIcon: {
			marginTop: -9,
			width: 26,
			height: 26,
			position: 'relative',
			bottom: -7,
			marginRight: 3,
			color: '#F4511E',
		},
		warningIcon: {
			marginTop: -9,
			width: 26,
			height: 26,
			position: 'relative',
			bottom: -7,
			marginRight: 3,
			color: '#FBC02D',
		},
	};
};
const formFields = {
	primaryStationTank: {},
	primaryDeliveryAmount: '',
	secondaryStationTank: {},
	secondaryDeliveryAmount: '',
	tertiaryStationTank: {},
	tertiaryDeliveryAmount: '',
	quaternaryStationTank: {},
	quaternaryDeliveryAmount: '',
	startDate: '',
	startTime: '',
	endDate: '',
	endTime: '',
	carrierId: '',

	fuelPickupType: '',
	firstTerminalTerminalId: '',
	firstTerminalSupplierId: '',
	firstTerminalLoadingNumber: '',
	firstTerminalSecondaryLoadingNumber: '',
	firstTerminalTertiaryLoadingNumber: '',
	firstTerminalQuaternaryLoadingNumber: '',
	firstTerminalAccountNumber: '',
	firstTerminalProductType: 'unl',

	firstTerminalEmailCopyOfDeliveryRequest: false,
	firstTerminalUseStationDeliveryIdAsLoadingNumber: false,
	firstTerminalRequireLoadingNumberPerStationOnSplitLoads: false,
	firstTerminalUseLoadingAssignments: false,
	firstTerminalUseAssignedLoadingNumbers: false,
	firstTerminalCarrierNeedsToProvidePickupWindow: false,
	firstTerminalBackups: [],

	secondTerminalTerminalId: '',
	secondTerminalSupplierId: '',
	secondTerminalLoadingNumber: '',
	secondTerminalSecondaryLoadingNumber: '',
	secondTerminalTertiaryLoadingNumber: '',
	secondTerminalQuaternaryLoadingNumber: '',
	secondTerminalAccountNumber: '',

	secondTerminalEmailCopyOfDeliveryRequest: false,
	secondTerminalUseStationDeliveryIdAsLoadingNumber: false,
	secondTerminalRequireLoadingNumberPerStationOnSplitLoads: false,
	secondTerminalUseLoadingAssignments: false,
	secondTerminalUseAssignedLoadingNumbers: false,
	secondTerminalCarrierNeedsToProvidePickupWindow: false,
	secondTerminalBackups: [],

	attachmentResourceIds: [],
	note: '',
	internalNote: '',
	date: '',
	allDayWindow: false,
};

const ALL_TECH_TERMINAL_ID = 27;

function calcE85Blend(totalE85) {
	const etohNeeded = totalE85 * 0.85;
	const carbobNeeded = totalE85 * 0.15;
	const gallonsOfEtohInCarbob = carbobNeeded * 0.1;
	const carbobWith10PercentEtoh = (carbobNeeded + gallonsOfEtohInCarbob) * 0.1;
	let etoh = etohNeeded - carbobWith10PercentEtoh;
	let unl = carbobNeeded + carbobWith10PercentEtoh;

	if (_.round(etoh) > etoh && _.round(unl) > unl) {
		etoh = Math.ceil(etoh);
		unl = Math.floor(unl);
	} else {
		etoh = _.round(etoh);
		unl = _.round(unl);
	}

	return {
		calculatedEtoh: etoh,
		calculatedCarbob: unl,
	};
}

function calcAllTechBlend(totalE85) {
	let etoh = totalE85 * 0.815;
	let unl = totalE85 * 0.185;

	if (_.round(etoh) > etoh && _.round(unl) > unl) {
		etoh = Math.ceil(etoh);
		unl = Math.floor(unl);
	} else {
		etoh = _.round(etoh);
		unl = _.round(unl);
	}

	return {
		calculatedEtoh: etoh,
		calculatedCarbob: unl,
	};
}

class SaveStationDelivery extends Component {
	constructor(props) {
		super(props);
		const stationDeliveryId = _.get(this.props, ['match', 'params', 'stationDeliveryId']);
		const stationId = _.get(this.props, ['match', 'params', 'stationId']);
		const tankNumber = _.get(this.props, ['match', 'params', 'tankNumber']);
		const productType = _.get(this.props, ['match', 'params', 'productType']);

		this.state = {
			stationDeliveryId: stationDeliveryId ? parseInt(stationDeliveryId, 10) : undefined,
			isUpdate: stationDeliveryId,
			//- how many stops past the first are on the form (0-3)
			extraLegCount: 0,
			formFieldError: {},
			isConfirmDialogOpen: false,
			isUpdatingDialogOpen: false,
			isCancelDialogOpen: false,
			isUncancelDialogOpen: false,
			isLockedEditDialogOpen: false,
			pendingLockSendAlert: false,
			updatingStatus: 'pending',
			estimate: undefined,
			secondaryEstimate: undefined,
			tertiaryEstimate: undefined,
			quaternaryEstimate: undefined,
			isFetchingEstimate: false,
			isFetchingSecondaryEstimate: false,
			isFetchingTertiaryEstimate: false,
			isFetchingQuaternaryEstimate: false,
			primaryStationChanged: false,
			secondaryStationChanged: false,
			tertiaryStationChanged: false,
			quaternaryStationChanged: false,
			assignmentStats: [],
		};
		this.state = _.merge(this.state, formFields);

		if (stationId && tankNumber && productType) {
			this.state.primaryStationTank = {
				stationId: parseInt(stationId, 10),
				tankNumber: parseInt(tankNumber, 10),
				productType,
			};
		}
	}

	//- Prefixes of the extra stops currently on the form, in order
	getExtraLegPrefixes = (state = this.state) => EXTRA_LEG_PREFIXES.slice(0, state.extraLegCount);

	//- Invoice lock: an issued/sent invoice freezes the delivery. Only the internal
	//- note stays editable without the canEditInvoicedDeliveries permission.
	isDeliveryLocked = () =>
		Boolean(this.state.isUpdate && deliveryLockLib.isDeliveryLocked(this.props.stationDelivery));

	canEditInvoicedDeliveries = () => deliveryLockLib.canEditInvoicedDeliveries(this.props.authUser);

	componentDidMount() {
		//- Only used for updates
		const {
			stationDelivery,
			getDelivery,
			stations,
			searchStations,
			carriers,
			searchCarriers,
			terminals,
			searchTerminals,
			getLoadingAssignmentStats,
		} = this.props;

		const { isUpdate, stationDeliveryId, primaryStationTank, assignmentStats } = this.state;

		if (!stations.length) {
			searchStations();
		}
		if (!carriers.length) {
			searchCarriers();
		}
		if (!terminals.length) {
			searchTerminals();
		}

		if (!_.isArray(assignmentStats)) {
			getLoadingAssignmentStats({
				callback: (err, r) => {
					if (err) {
						return this.props.showErrors([err]);
					}

					/** @namespace r.assignmentsStats **/
					this.setState({ assignmentStats: r.assignmentStats });
				},
			});
		}

		//- Just an add. But we might start with a station
		if (!isUpdate) {
			if (!_.isEmpty(primaryStationTank)) {
				this.setState({ isFetchingEstimate: true });
			}
			return;
		}

		//- This is an update so fetch the delivery
		if (!stationDelivery) {
			getDelivery({
				params: { stationDeliveryIds: [stationDeliveryId] },
			});
			return;
		}

		//
		//- Its an update and we have the delivery info already
		//
		const newState = this.getFormValuesFromDelivery({ stationDelivery });
		newState.isFetchingEstimate = true;
		EXTRA_LEG_PREFIXES.forEach((legPrefix) => {
			if (!_.isEmpty(newState[`${legPrefix}StationTank`])) {
				newState[`isFetching${upperFirst(legPrefix)}Estimate`] = true;
			}
		});
		this.setState(newState);
	}

	/**
	 * Checks and updates the estimate panels
	 */
	componentDidUpdate(prevProps, prevState) {
		const { stationDelivery } = this.props;

		//- isUpdate and fetching the delivery finished
		if (_.isUndefined(prevProps.stationDelivery) && stationDelivery) {
			const newState = this.getFormValuesFromDelivery({ stationDelivery });
			newState.isFetchingEstimate = true;
			EXTRA_LEG_PREFIXES.forEach((legPrefix) => {
				if (!_.isEmpty(newState[`${legPrefix}StationTank`])) {
					newState[`isFetching${upperFirst(legPrefix)}Estimate`] = true;
				}
			});
			this.setState(newState);
		}

		//- Need to update the estimate
		if (prevState.isFetchingEstimate === false && this.state.isFetchingEstimate === true) {
			let primaryStationChanged = false;
			if (prevState.primaryStationChanged === false && this.state.primaryStationChanged === true) {
				primaryStationChanged = true;
			}

			this.getEstimateAndLastDelivery({
				legPrefix: 'primary',
				stationChanged: primaryStationChanged,
			});
		}

		//- One estimate panel per extra stop
		EXTRA_LEG_PREFIXES.forEach((legPrefix) => {
			const fetchingKey = `isFetching${upperFirst(legPrefix)}Estimate`;
			const changedKey = `${legPrefix}StationChanged`;
			if (prevState[fetchingKey] === false && this.state[fetchingKey] === true) {
				const stationChanged =
					prevState[changedKey] === false && this.state[changedKey] === true;
				this.getEstimateAndLastDelivery({
					legPrefix,
					stationChanged,
				});
			}
		});
	}

	getEstimateParams = (p) => {
		const { legPrefix } = p;
		const isPrimary = legPrefix === 'primary';

		let { startDate, startTime, endDate, endTime, allDayWindow } = this.state;

		const stationTank = this.state[`${legPrefix}StationTank`];
		const legDeliveryAmount = this.state[`${legPrefix}DeliveryAmount`];

		let params = {};

		if (_.isEmpty(stationTank)) {
			return false;
		}

		const { stationId, tankNumber, productType } = stationTank;
		params = { stationId, tankNumber, productType };
		if (!isPrimary) {
			params.isSecondary = true;
		}

		//- >= so a saved 1000 gallon delivery is still sent and echoed back,
		//- otherwise the estimate recommends its own volume for the form
		const deliveryAmount = _.parseInt(legDeliveryAmount, 10);
		if (deliveryAmount && deliveryAmount >= 1000) {
			params.deliveryAmount = parseInt(legDeliveryAmount, 10);
		}

		//- window
		if (startDate && allDayWindow) {
			const deliveryWindowStart = moment(startDate).startOf('day').utc();
			const deliveryWindowEnd = moment(startDate).endOf('day').utc();
			params.deliveryWindowStartDateTime = deliveryWindowStart.format('YYYY-MM-DD HH:mm');
			params.deliveryWindowEndDateTime = deliveryWindowEnd.format('YYYY-MM-DD HH:mm');
		} else if (startDate && startTime && endDate && endTime) {
			const sd = moment(startDate).format('YYYY-MM-DD');
			const st = moment(startTime).format('HH:mm');
			const deliveryWindowStart = moment(`${sd} ${st}`, 'YYYY-MM-DD HH:mm').utc();

			const ed = moment(endDate).format('YYYY-MM-DD');
			const et = moment(endTime).format('HH:mm');
			const deliveryWindowEnd = moment(`${ed} ${et}`, 'YYYY-MM-DD HH:mm').utc();

			params.deliveryWindowStartDateTime = deliveryWindowStart.format('YYYY-MM-DD HH:mm');
			params.deliveryWindowEndDateTime = deliveryWindowEnd.format('YYYY-MM-DD HH:mm');
		}
		return params;
	};

	getEstimateAndLastDelivery = (p) => {
		const { legPrefix, stationChanged } = p;
		const isPrimary = legPrefix === 'primary';
		const primaryStationChanged = isPrimary && stationChanged;

		//- the state keys this leg's estimate machinery lives under
		const fetchingKey = isPrimary ? 'isFetchingEstimate' : `isFetching${upperFirst(legPrefix)}Estimate`;
		const changedKey = `${legPrefix}StationChanged`;

		const { estimateDelivery, showErrors } = this.props;
		const params = this.getEstimateParams({ legPrefix });

		const newState = {};
		if (!params) {
			newState[fetchingKey] = false;
			newState[changedKey] = false;
			this.setState(newState);
			return;
		}

		const {
			stationId,
			tankNumber,
			deliveryAmount,
			deliveryWindowStartDateTime,
			deliveryWindowEndDateTime,
		} = params;

		const formFieldError = _.clone(this.state.formFieldError);
		newState.formFieldError = formFieldError;
		['startTime', 'startDate', 'endTime', 'endDate'].forEach((s) => {
			delete formFieldError[s];
		});

		//- Check for form errors
		if (deliveryWindowStartDateTime && deliveryWindowEndDateTime) {
			const start = moment(deliveryWindowStartDateTime, 'YYYY-MM-DD HH:mm');
			const end = moment(deliveryWindowEndDateTime, 'YYYY-MM-DD HH:mm');
			if (start.isAfter(end)) {
				const error = 'Start date must be before end date';
				formFieldError['startTime'] = error;
				formFieldError['startDate'] = error;
			}
		}

		//- Error
		if (formFieldError['startTime']) {
			newState[fetchingKey] = false;
			newState[changedKey] = false;
			this.setState(newState);
			return;
		}

		async.waterfall(
			[
				(callback) => {
					setTimeout(callback, 300); //slight delay
				},
				(callback) => {
					let estimateParams = {
						stationIds: [stationId],
						deliveryAmount,
						deliveryWindowStartDateTime,
						deliveryWindowEndDateTime,
					};

					if (primaryStationChanged) {
						delete estimateParams.deliveryAmount;
						delete estimateParams.deliveryWindowStartDateTime;
						delete estimateParams.deliveryWindowEndDateTime;
					}

					if (!isPrimary && stationChanged) {
						delete estimateParams.deliveryAmount;
					}

					estimateDelivery({
						params: estimateParams,
						callback,
					});
				},
				(r, callback) => {
					let estimate = _.find(r, { stationId, tankNumber }) || {};
					let newState = {};

					//- Go through the last StationDelivery and set values
					const {
						lastStationDelivery,
						recommendedDeliveryVolume,
						recommendedStartWindow,
						recommendedEndWindow,
					} = estimate;

					//- Locked (invoiced) deliveries keep the form faithful to the saved
					//- record — never auto-fill from the last delivery or recommendations
					const isLockedDelivery = this.isDeliveryLocked();

					// An extra stop's estimate just updates that stop's delivery amount.
					// A new station for the stop replaces the amount, so the previous
					// station's gallons never ride along (blank when there's nothing to suggest)
					if (!isPrimary) {
						const amountKey = `${legPrefix}DeliveryAmount`;
						if ((stationChanged || !this.state[amountKey]) && !isLockedDelivery) {
							if (recommendedDeliveryVolume) {
								newState[amountKey] = recommendedDeliveryVolume;
							} else if (lastStationDelivery && lastStationDelivery.primaryDeliveryAmount) {
								newState[amountKey] = lastStationDelivery.primaryDeliveryAmount;
							} else if (stationChanged) {
								newState[amountKey] = '';
							}
						}
						newState[`${legPrefix}Estimate`] = estimate;
						newState[fetchingKey] = false;
						newState[changedKey] = false;

						this.setState(newState);
						return callback();
					}

					//
					//- Primary estimate. Things get toggled
					//

					//- If fuelPickupType is singleFuelPickup. The don't populate second terminal
					const { fuelPickupType, isUpdate } = this.state;

					//- Has a last stationDelivery
					if (lastStationDelivery && !isLockedDelivery) {
						[
							'primaryDeliveryAmount',
							'carrierId',

							'fuelPickupType',
							'firstTerminalProductType',
							'firstTerminalTerminalId',
							'firstTerminalSupplierId',
							'firstTerminalBackups',

							'secondTerminalTerminalId',
							'secondTerminalSupplierId',
							'secondTerminalBackups',

							'note',
							//'internalNote',
							'allDayWindow',
						].forEach((key) => {
							if (_.isNil(lastStationDelivery[key])) {
								return;
							}

							//- Primary station changed. Update everything
							if (primaryStationChanged) {
								newState[key] = lastStationDelivery[key];
								return;
							}

							//- Has value of false so it will always override to true
							if (key === 'allDayWindow') {
								return;
							}

							//- Field change don't override any settings we already have
							if (_.isArray(this.state[key]) && this.state[key].length) {
								return;
							}
							if (!_.isArray(this.state[key]) && this.state[key]) {
								return;
							}

							//- Do not populate second terminal info if singlePickupType
							if (fuelPickupType === 'singleFuelPickup') {
								if (
									key === 'secondTerminalTerminalId' ||
									key === 'secondTerminalSupplierId' ||
									key === 'secondTerminalBackups'
								) {
									return;
								}
							}

							if (isUpdate && (key === 'note' || key === 'internalNote')) {
								return;
							}

							newState[key] = lastStationDelivery[key];
						});

						//- The user just picked Station #1: bring over the extra stops from
						//- its last delivery (if not set) and show them. Any other refresh
						//- (window, amount, opening from Monitoring) leaves the stops alone,
						//- otherwise SPLIT LOAD would open on a hidden, stale stop
						if (primaryStationChanged) {
							const lastLegs = getDeliveryLegs(lastStationDelivery);
							lastLegs
								.filter((leg) => leg.legNumber >= 2)
								.forEach((leg) => {
									const lastLegPrefix = EXTRA_LEG_PREFIXES[leg.legNumber - 2];
									if (!_.isEmpty(this.state[`${lastLegPrefix}StationTank`])) {
										return;
									}
									newState[`${lastLegPrefix}StationTank`] = {
										stationId: leg.stationId,
										tankNumber: leg.tankNumber,
										productType: leg.productType,
									};
									//- the stop's amount travels with its station, unless the user typed one
									if (!this.state[`${lastLegPrefix}DeliveryAmount`]) {
										newState[`${lastLegPrefix}DeliveryAmount`] = leg.deliveryAmount || '';
									}
									newState.extraLegCount = Math.max(
										newState.extraLegCount || this.state.extraLegCount,
										leg.legNumber - 1
									);
									newState[`isFetching${upperFirst(lastLegPrefix)}Estimate`] = true;
								});

							this.getExtraLegPrefixes().forEach((extraPrefix) => {
								newState[`isFetching${upperFirst(extraPrefix)}Estimate`] = true;
							});
						}
					}

					//- Only if we have an estimate
					if (recommendedStartWindow && recommendedEndWindow && !isLockedDelivery) {
						/*
					const startMoment = moment(recommendedStartWindow);
					const endMoment = moment(recommendedEndWindow);
					newState.startDate = startMoment.toDate();
					newState.startTime = startMoment.toDate();
					newState.endDate = endMoment.toDate();
					newState.endTime = endMoment.toDate();
					*/

						newState.startDate = recommendedStartWindow;
						newState.startTime = recommendedStartWindow;
						newState.endDate = recommendedEndWindow;
						newState.endTime = recommendedEndWindow;
					} else {
						//- Lets attempt to fill in
						if (primaryStationChanged && lastStationDelivery && !isLockedDelivery) {
							const lastStart = moment(lastStationDelivery.deliveryWindowStartDateTime);
							const lastEnd = moment(lastStationDelivery.deliveryWindowEndDateTime);

							const startMoment = moment().add(4, 'days');
							const endMoment = moment().add(4, 'days');
							startMoment.hour(lastStart.hour()).minute(0).second(0);
							endMoment.hour(lastEnd.hour()).minute(0).second(0);

							newState.startDate = startMoment.format('YYYY-MM-DD HH:mm');
							newState.startTime = startMoment.format('YYYY-MM-DD HH:mm');
							newState.endDate = endMoment.format('YYYY-MM-DD HH:mm');
							newState.endTime = endMoment.format('YYYY-MM-DD HH:mm');
						}
					}

					//- Done. Never clobber an amount already on the form (a saved
					//- delivery being updated) unless the user picked a new station
					if (
						recommendedDeliveryVolume &&
						!isLockedDelivery &&
						(primaryStationChanged || !this.state.primaryDeliveryAmount)
					) {
						newState.primaryDeliveryAmount = recommendedDeliveryVolume;
					}
					newState.estimate = estimate;
					newState.isFetchingEstimate = false;
					newState.primaryStationChanged = false;

					this.setState(newState);

					return callback();
				},
			],
			(err) => {
				if (err) {
					return showErrors([err]);
				}
			}
		);
	};

	onTerminalPickerChange = (terminalPrefix, r) => {
		let newState = {
			[`${terminalPrefix}TerminalId`]: r.terminalId,
			[`${terminalPrefix}SupplierId`]: r.supplierId,
			[`${terminalPrefix}LoadingNumber`]: r.loadingNumber,
			[`${terminalPrefix}SecondaryLoadingNumber`]: r.secondaryLoadingNumber,
			[`${terminalPrefix}TertiaryLoadingNumber`]: r.tertiaryLoadingNumber,
			[`${terminalPrefix}QuaternaryLoadingNumber`]: r.quaternaryLoadingNumber,
			[`${terminalPrefix}AccountNumber`]: r.accountNumber,

			[`${terminalPrefix}EmailCopyOfDeliveryRequest`]: r.emailCopyOfDeliveryRequest,
			[`${terminalPrefix}UseStationDeliveryIdAsLoadingNumber`]:
				r.useStationDeliveryIdAsLoadingNumber,
			[`${terminalPrefix}RequireLoadingNumberPerStationOnSplitLoads`]:
				r.requireLoadingNumberPerStationOnSplitLoads,
			[`${terminalPrefix}UseLoadingAssignments`]: r.useLoadingAssignments,
			[`${terminalPrefix}UseAssignedLoadingNumbers`]: r.useAssignedLoadingNumbers,
			[`${terminalPrefix}CarrierNeedsToProvidePickupWindow`]: r.carrierNeedsToProvidePickupWindow,

			[`${terminalPrefix}Backups`]: r.backups,
		};

		newState = _.pickBy(newState, (v) => {
			return !_.isUndefined(v);
		});
		if (_.keys(newState).length) {
			this.validateAndUpdate({ newState });
		}
	};

	onFirstTerminalChange = (r) => {
		this.onTerminalPickerChange('firstTerminal', r);
	};

	onSecondTerminalChange = (r) => {
		this.onTerminalPickerChange('secondTerminal', r);
	};

	handleAttachmentUpload = ({ resource }) => {
		const attachmentResourceIds = this.state.attachmentResourceIds.slice();
		/** @namespace resource.resourceId **/
		attachmentResourceIds.push(resource.resourceId);
		this.setState({ attachmentResourceIds });
	};

	handleAddSplitLeg = () => {
		const { extraLegCount } = this.state;
		if (extraLegCount >= EXTRA_LEG_PREFIXES.length) {
			return;
		}
		this.setState({ extraLegCount: extraLegCount + 1 });
	};

	//- Remove one extra stop; later stops (and their loading numbers) shift down
	handleRemoveSplitLeg = (removeIndex) => {
		const { extraLegCount } = this.state;

		//- the remaining extra stops, in order, with everything that travels with a stop
		const remaining = [];
		for (let i = 0; i < extraLegCount; i++) {
			if (i === removeIndex) {
				continue;
			}
			const legPrefix = EXTRA_LEG_PREFIXES[i];
			remaining.push({
				stationTank: this.state[`${legPrefix}StationTank`],
				deliveryAmount: this.state[`${legPrefix}DeliveryAmount`],
				estimate: this.state[`${legPrefix}Estimate`],
				firstTerminalLoadingNumber: this.state[loadingNumberField('firstTerminal', i + 2)],
				secondTerminalLoadingNumber: this.state[loadingNumberField('secondTerminal', i + 2)],
			});
		}

		const newState = { extraLegCount: extraLegCount - 1 };
		EXTRA_LEG_PREFIXES.forEach((legPrefix, i) => {
			const leg = remaining[i];
			newState[`${legPrefix}StationTank`] = leg ? leg.stationTank : {};
			newState[`${legPrefix}DeliveryAmount`] = leg ? leg.deliveryAmount : '';
			newState[`${legPrefix}Estimate`] = leg ? leg.estimate : undefined;
			newState[loadingNumberField('firstTerminal', i + 2)] = leg
				? leg.firstTerminalLoadingNumber
				: '';
			newState[loadingNumberField('secondTerminal', i + 2)] = leg
				? leg.secondTerminalLoadingNumber
				: '';
		});
		this.setState(newState);
	};

	closeConfirmDialog = () => {
		this.setState({ isConfirmDialogOpen: false });
	};

	closeUpdatingDialog = () => {
		this.setState({ isUpdatingDialogOpen: false });
	};

	closeCancelDialog = () => {
		this.setState({ isCancelDialogOpen: false });
	};

	handleCancel = () => {
		this.setState({ isCancelDialogOpen: true });
	};

	closeUncancelDialog = () => {
		this.setState({ isUncancelDialogOpen: false });
	};

	handleUncancel = () => {
		this.setState({ isUncancelDialogOpen: true });
	};

	handleSave = ({ sendAlert, lockOverrideReason }) => {
		const { addDelivery, updateDelivery, showErrors } = this.props;

		//- Required fields
		const formFieldError = {};
		const requiredFields = [
			{ fieldName: 'primaryStationTank', display: 'Station #1' },
			{ fieldName: 'primaryDeliveryAmount', display: 'Delivery amount' },
			{ fieldName: 'startDate', display: 'Start Date' },
			{ fieldName: 'carrierId', display: 'Carrier' },
			{ fieldName: 'firstTerminalTerminalId', display: 'First Terminal' },
		];

		//- Get all the values and do individual checks
		const {
			primaryStationTank,
			primaryDeliveryAmount,
			startDate,
			startTime,
			endDate,
			endTime,
			carrierId,

			fuelPickupType,
			firstTerminalTerminalId,
			firstTerminalSupplierId,
			firstTerminalLoadingNumber,
			firstTerminalSecondaryLoadingNumber,
			firstTerminalTertiaryLoadingNumber,
			firstTerminalQuaternaryLoadingNumber,
			firstTerminalAccountNumber,
			firstTerminalProductType,

			firstTerminalEmailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber,
			firstTerminalRequireLoadingNumberPerStationOnSplitLoads,
			firstTerminalUseLoadingAssignments,
			firstTerminalUseAssignedLoadingNumbers,
			firstTerminalCarrierNeedsToProvidePickupWindow,
			firstTerminalBackups,

			secondTerminalTerminalId,
			secondTerminalSupplierId,
			secondTerminalLoadingNumber,
			secondTerminalSecondaryLoadingNumber,
			secondTerminalTertiaryLoadingNumber,
			secondTerminalQuaternaryLoadingNumber,
			secondTerminalAccountNumber,

			secondTerminalEmailCopyOfDeliveryRequest,
			secondTerminalUseStationDeliveryIdAsLoadingNumber,
			secondTerminalRequireLoadingNumberPerStationOnSplitLoads,
			secondTerminalUseLoadingAssignments,
			secondTerminalUseAssignedLoadingNumbers,
			secondTerminalCarrierNeedsToProvidePickupWindow,
			secondTerminalBackups,

			note,
			internalNote,
			attachmentResourceIds,
			//- for updates
			isUpdate,
			stationDeliveryId,
			allDayWindow,
		} = this.state;

		const extraLegPrefixes = this.getExtraLegPrefixes();

		const primaryStationId = primaryStationTank.stationId;
		const primaryTankNumber = primaryStationTank.tankNumber;
		const primaryProductType = primaryStationTank.productType;

		if (primaryProductType === 'e85' && fuelPickupType === 'dualFuelPickup') {
			requiredFields.push({ fieldName: 'secondTerminalTerminalId', display: 'Second Terminal' });
		}

		if (!this.state.allDayWindow) {
			requiredFields.push(
				{ fieldName: 'startTime', display: 'Start Time' },
				{ fieldName: 'endDate', display: 'End Date' },
				{ fieldName: 'endTime', display: 'End Time' }
			);
		}

		requiredFields.forEach((s) => {
			if (!this.state[s.fieldName]) {
				formFieldError[s.fieldName] = `${s.display} is required`;
			}
		});

		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			showErrors(_.values(formFieldError));
			return;
		}

		let deliveryWindowStartDateTime, deliveryWindowEndDateTime;

		if (allDayWindow) {
			const deliveryWindowStart = moment(startDate).startOf('day').utc();
			const deliveryWindowEnd = moment(startDate).endOf('day').utc();
			deliveryWindowStartDateTime = deliveryWindowStart.format('YYYY-MM-DD HH:mm');
			deliveryWindowEndDateTime = deliveryWindowEnd.format('YYYY-MM-DD HH:mm');
		} else {
			const sd = moment(startDate).format('YYYY-MM-DD');
			const st = moment(startTime).format('HH:mm');
			deliveryWindowStartDateTime = moment(`${sd} ${st}`, 'YYYY-MM-DD HH:mm')
				.utc()
				.format('YYYY-MM-DD HH:mm');

			const ed = moment(endDate).format('YYYY-MM-DD');
			const et = moment(endTime).format('HH:mm');
			deliveryWindowEndDateTime = moment(`${ed} ${et}`, 'YYYY-MM-DD HH:mm')
				.utc()
				.format('YYYY-MM-DD HH:mm');
		}

		const params = {
			primaryStationId,
			primaryTankNumber,
			primaryProductType,
			primaryDeliveryAmount: _.isString(primaryDeliveryAmount)
				? parseInt(primaryDeliveryAmount.replace(/,/g, ''), 10)
				: parseInt(primaryDeliveryAmount, 10),
			deliveryWindowStartDateTime,
			deliveryWindowEndDateTime,
			carrierId,

			firstTerminalTerminalId: parseInt(firstTerminalTerminalId, 10),
			firstTerminalSupplierId: parseInt(firstTerminalSupplierId, 10),
			firstTerminalLoadingNumber,
			firstTerminalSecondaryLoadingNumber,
			firstTerminalTertiaryLoadingNumber,
			firstTerminalQuaternaryLoadingNumber,
			firstTerminalAccountNumber,

			firstTerminalEmailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber,
			firstTerminalRequireLoadingNumberPerStationOnSplitLoads,
			firstTerminalUseLoadingAssignments,
			firstTerminalUseAssignedLoadingNumbers,
			firstTerminalCarrierNeedsToProvidePickupWindow,
			firstTerminalBackups,

			note,
			internalNote,
			attachmentResourceIds,
			isHidden: false,
			sendAlert,
			allDayWindow,
		};

		params.fuelPickupType = fuelPickupType ? fuelPickupType : 'singleFuelPickup';

		if (
			primaryProductType === 'e85' &&
			secondTerminalTerminalId &&
			fuelPickupType !== 'singleFuelPickup'
		) {
			params.firstTerminalProductType = firstTerminalProductType;
			params.secondTerminalProductType = 'etoh';

			params.secondTerminalTerminalId = parseInt(secondTerminalTerminalId, 10);
			params.secondTerminalSupplierId = parseInt(secondTerminalSupplierId, 10);
			params.secondTerminalLoadingNumber = secondTerminalLoadingNumber;
			params.secondTerminalSecondaryLoadingNumber = secondTerminalSecondaryLoadingNumber;
			params.secondTerminalTertiaryLoadingNumber = secondTerminalTertiaryLoadingNumber;
			params.secondTerminalQuaternaryLoadingNumber = secondTerminalQuaternaryLoadingNumber;
			params.secondTerminalAccountNumber = secondTerminalAccountNumber;

			params.secondTerminalEmailCopyOfDeliveryRequest = secondTerminalEmailCopyOfDeliveryRequest;
			params.secondTerminalUseStationDeliveryIdAsLoadingNumber = secondTerminalUseStationDeliveryIdAsLoadingNumber;
			params.secondTerminalRequireLoadingNumberPerStationOnSplitLoads = secondTerminalRequireLoadingNumberPerStationOnSplitLoads;

			params.secondTerminalUseLoadingAssignments = secondTerminalUseLoadingAssignments;
			params.secondTerminalUseAssignedLoadingNumbers = secondTerminalUseAssignedLoadingNumbers;
			params.secondTerminalCarrierNeedsToProvidePickupWindow = secondTerminalCarrierNeedsToProvidePickupWindow;
			params.secondTerminalBackups = secondTerminalBackups;
		} else {
			params.firstTerminalProductType = primaryProductType;
		}

		//- Extra stops: each needs a station + amount, all on the same product,
		//- and the stops must be contiguous (no blank station before a filled one —
		//- remove the empty section instead)
		const filledLegPrefixes = extraLegPrefixes.filter(
			(legPrefix) => !_.isEmpty(this.state[`${legPrefix}StationTank`])
		);
		let hasLegErrors = false;
		extraLegPrefixes.forEach((legPrefix, i) => {
			const stationNumberLabel = i + 2;
			const stationTank = this.state[`${legPrefix}StationTank`];
			const legAmount = this.state[`${legPrefix}DeliveryAmount`];

			if (_.isEmpty(stationTank)) {
				const laterFilled = extraLegPrefixes
					.slice(i + 1)
					.some((laterPrefix) => !_.isEmpty(this.state[`${laterPrefix}StationTank`]));
				if (laterFilled) {
					formFieldError[`${legPrefix}StationTank`] =
						`Station #${stationNumberLabel} is empty but a later station is set — ` +
						`fill it or remove the empty station`;
					hasLegErrors = true;
				}
				return;
			}

			if (!legAmount) {
				formFieldError[`${legPrefix}DeliveryAmount`] =
					`Station #${stationNumberLabel} delivery amount required`;
				hasLegErrors = true;
				return;
			}

			if (primaryProductType !== stationTank.productType) {
				formFieldError[`${legPrefix}DeliveryAmount`] =
					`All stations on a split load need to be the same product`;
				hasLegErrors = true;
			}
		});
		if (hasLegErrors) {
			this.setState({ formFieldError });
			showErrors(_.values(formFieldError));
			return;
		}

		filledLegPrefixes.forEach((legPrefix) => {
			const stationTank = this.state[`${legPrefix}StationTank`];
			const legAmount = this.state[`${legPrefix}DeliveryAmount`];

			params[`${legPrefix}StationId`] = stationTank.stationId;
			params[`${legPrefix}TankNumber`] = stationTank.tankNumber;
			params[`${legPrefix}ProductType`] = stationTank.productType;
			params[`${legPrefix}DeliveryAmount`] = _.isString(legAmount)
				? parseInt(legAmount.replace(/,/g, ''), 10)
				: parseInt(legAmount, 10);
		});

		if (filledLegPrefixes.length) {
			//- clears any stops past the ones on the form (update only)
			params.keepLegCount = 1 + filledLegPrefixes.length;
		} else {
			params.doNotSplitLoad = true;
		}

		if (!isUpdate) {
			async.waterfall(
				[
					(callback) => {
						addDelivery({
							params,
							callback,
						});
					},
					(r, callback) => {
						const confirmingStationDelivery = _.get(r, [
							'collections',
							'stationDeliveryCollection',
							0,
						]);
						if (!confirmingStationDelivery) {
							return callback('Fatal error. Missing stationDelivery.');
						}

						this.showCarrierEmail({
							confirmingStationDelivery,
							callback,
						});
					},
				],
				(e) => {
					if (e) {
						showErrors([e]);
					}
				}
			);
		} else {
			//- Locked delivery: collect the required reason up front (the server
			//- still enforces it; internal-note-only saves ignore the reason)
			if (this.isDeliveryLocked() && this.canEditInvoicedDeliveries() && !lockOverrideReason) {
				this.setState({
					isLockedEditDialogOpen: true,
					pendingLockSendAlert: !!sendAlert,
				});
				return;
			}

			this.setState({
				isUpdatingDialogOpen: true,
				updatingStatus: 'updating',
			});

			params.stationDeliveryId = stationDeliveryId;
			if (lockOverrideReason) {
				params.lockOverrideReason = lockOverrideReason;
			}
			async.waterfall(
				[
					(callback) => {
						updateDelivery({
							params,
							callback,
						});
					},
				],
				(err) => {
					//- errors already show
					if (err) {
						//- Invoice lock fallback (invoice issued while the page was open):
						//- the server wants a reason — collect it and retry the same save
						if (deliveryLockLib.isLockReasonRequiredError(err) && this.canEditInvoicedDeliveries()) {
							this.setState({
								isUpdatingDialogOpen: false,
								updatingStatus: 'pending',
								isLockedEditDialogOpen: true,
								pendingLockSendAlert: !!sendAlert,
							});
							return;
						}
						showErrors(err);
						console.error('There was an error', err);
						this.closeUpdatingDialog();
						return;
					}
					if (sendAlert) {
						this.setState({ updatingStatus: 'doneWithAlert' });
					} else {
						this.setState({ updatingStatus: 'done' });
					}
				}
			);
		}
	};

	showCarrierEmail = ({ confirmingStationDelivery, callback }) => {
		const { getStationDeliveryCarrierEmail } = this.props;
		async.waterfall(
			[
				(callback) => {
					getStationDeliveryCarrierEmail({
						params: {
							stationDeliveryIds: [confirmingStationDelivery.stationDeliveryId],
						},
						callback,
					});
				},
				(r, callback) => {
					const confirmingJobResult = _.get(r, ['jobResults', 0]);

					if (!confirmingJobResult) {
						return callback('Did not find Carrier emails.');
					}

					this.setState({
						isConfirmDialogOpen: true,
						confirmingStationDelivery,
						confirmingJobResult,
					});
					return callback();
				},
			],
			callback
		);
	};

	onChange = (event, fieldName, index) => {
		const { type, value, checked } = event.target;
		const val = type === 'checkbox' ? checked : value;
		let newState = {};

		if (!_.isNumber(index)) {
			newState = { [fieldName]: val };
		} else {
			const inputs = this.state[fieldName].slice();
			inputs[index] = val;
			newState = { [fieldName]: inputs };
		}

		if (fieldName === 'allDayWindow') {
			if (val === true) {
				newState.startTime = '';
				newState.endTime = '';
				newState.endDate = '';
			}

			newState.isFetchingEstimate = true;
			this.getExtraLegPrefixes().forEach((legPrefix) => {
				newState[`isFetching${upperFirst(legPrefix)}Estimate`] = true;
			});
		}

		if (fieldName === 'primaryStationTank' && value) {
			newState.primaryStationChanged = true;
			newState.isFetchingEstimate = true;
		}

		if (fieldName === 'primaryDeliveryAmount') {
			const amount = parseInt(value, 10);
			if (amount && amount >= 1000 && amount < 9999) {
				newState.isFetchingEstimate = true;
			}
		}

		EXTRA_LEG_PREFIXES.forEach((legPrefix) => {
			if (fieldName === `${legPrefix}StationTank` && value) {
				newState[`${legPrefix}StationChanged`] = true;
				newState[`isFetching${upperFirst(legPrefix)}Estimate`] = true;
			}

			if (fieldName === `${legPrefix}DeliveryAmount`) {
				const amount = parseInt(value, 10);
				if (amount && amount >= 1000 && amount < 9999) {
					newState[`isFetching${upperFirst(legPrefix)}Estimate`] = true;
				}
			}
		});

		if (fieldName === 'carrierId') {
			this.validateAndUpdate({
				newState: { carrierId: event.target.value },
			});
		}
		this.setState(newState);
	};

	onPureChange = (value, path) => {
		const arrayPath = _.toPath(path);
		const basePath = _.head(arrayPath);
		let base = this.state[basePath];
		let newState = {};

		if (_.isArray(base) || _.isObject(base)) {
			base = _.cloneDeep(base);
			_.set(base, arrayPath.slice(1), value);
			newState[basePath] = base;
		} else {
			newState[basePath] = value;
		}

		if (path === 'fuelPickupType') {
			['firstTerminal', 'secondTerminal'].forEach((terminalPrefix) => {
				newState[`${terminalPrefix}TerminalId`] = '';
				newState[`${terminalPrefix}SupplierId`] = '';
				newState[`${terminalPrefix}Backups`] = [];
				newState[`${terminalPrefix}LoadingNumber`] = '';
				newState[`${terminalPrefix}SecondaryLoadingNumber`] = '';
				newState[`${terminalPrefix}TertiaryLoadingNumber`] = '';
				newState[`${terminalPrefix}QuaternaryLoadingNumber`] = '';
				newState[`${terminalPrefix}AccountNumber`] = '';

				newState[`${terminalPrefix}EmailCopyOfDeliveryRequest`] = false;
				newState[`${terminalPrefix}UseStationDeliveryIdAsLoadingNumber`] = false;
				newState[`${terminalPrefix}RequireLoadingNumberPerStationOnSplitLoads`] = false;
				newState[`${terminalPrefix}UseLoadingAssignments`] = false;
				newState[`${terminalPrefix}CarrierNeedsToProvidePickupWindow`] = false;
			});
		}

		this.setState(newState);
	};

	onStationTankChange = (value, path) => {
		if (!value) {
			return;
		}

		const newState = { [[path]]: value };
		//const { stationId, tankNumber, productType } = r;

		if (path === 'primaryStationTank') {
			newState.primaryStationChanged = true;
			newState.isFetchingEstimate = true;
		}

		EXTRA_LEG_PREFIXES.forEach((legPrefix) => {
			if (path === `${legPrefix}StationTank`) {
				newState[`${legPrefix}StationChanged`] = true;
				newState[`isFetching${upperFirst(legPrefix)}Estimate`] = true;
			}
		});

		this.setState(newState);
	};

	//- triggers isFetching
	onDateTimeChange = (value, path) => {
		const arrayPath = _.isString(path) ? _.toPath(path) : path;

		const val = _.isObject(value) && value.target ? value.target.value : value;

		const basePath = _.head(arrayPath);
		let base = this.state[basePath];

		const newState = {
			isFetchingEstimate: true,
		};

		if (_.isArray(base) || _.isObject(base)) {
			base = _.cloneDeep(base);
			_.set(base, arrayPath.slice(1), val);
			newState[basePath] = base;
		} else {
			newState[basePath] = val;
		}

		this.getExtraLegPrefixes().forEach((legPrefix) => {
			newState[`isFetching${upperFirst(legPrefix)}Estimate`] = true;
		});
		this.setState(newState);
	};

	onUnleadedProductTypeChange = (value, path) => {
		if (!value) {
			return;
		}
		const newState = { [[path]]: value };
		newState.firstTerminalTerminalId = '';
		newState.firstTerminalSupplierId = '';
		newState.firstTerminalBackups = [];

		this.setState(newState);
	};

	getFormValuesFromDelivery = (props) => {
		const { stationDelivery } = props;
		if (!stationDelivery) {
			return;
		}

		const formValues = {};

		//- One to one
		[
			'primaryDeliveryAmount',
			'secondaryDeliveryAmount',
			'tertiaryDeliveryAmount',
			'quaternaryDeliveryAmount',
			'carrierId',

			'fuelPickupType',
			'firstTerminalTerminalId',
			'firstTerminalSupplierId',
			'firstTerminalLoadingNumber',
			'firstTerminalAccountNumber',
			'firstTerminalSecondaryLoadingNumber',
			'firstTerminalTertiaryLoadingNumber',
			'firstTerminalQuaternaryLoadingNumber',
			'firstTerminalProductType',

			'firstTerminalEmailCopyOfDeliveryRequest',
			'firstTerminalUseStationDeliveryIdAsLoadingNumber',
			'firstTerminalRequireLoadingNumberPerStationOnSplitLoads',
			'firstTerminalUseLoadingAssignments',
			'firstTerminalUseAssignedLoadingNumbers',
			'firstTerminalCarrierNeedsToProvidePickupWindow',
			'firstTerminalBackups',

			'secondTerminalTerminalId',
			'secondTerminalSupplierId',
			'secondTerminalLoadingNumber',
			'secondTerminalAccountNumber',
			'secondTerminalSecondaryLoadingNumber',
			'secondTerminalTertiaryLoadingNumber',
			'secondTerminalQuaternaryLoadingNumber',

			'secondTerminalEmailCopyOfDeliveryRequest',
			'secondTerminalUseStationDeliveryIdAsLoadingNumber',
			'secondTerminalRequireLoadingNumberPerStationOnSplitLoads',
			'secondTerminalUseLoadingAssignments',
			'secondTerminalUseAssignedLoadingNumbers',
			'secondTerminalCarrierNeedsToProvidePickupWindow',
			'secondTerminalBackups',

			'attachmentResourceIds',
			'note',
			'internalNote',
			'allDayWindow',
		].forEach((key) => {
			if (_.isNil(stationDelivery[key])) {
				return;
			}
			formValues[key] = stationDelivery[key];
		});

		const { deliveryWindowStartDateTime, deliveryWindowEndDateTime, allDayWindow } =
			stationDelivery;

		//- Stations — one per stop
		const legs = getDeliveryLegs(stationDelivery);
		legs.forEach((leg) => {
			const legPrefix = leg.legNumber === 1 ? 'primary' : EXTRA_LEG_PREFIXES[leg.legNumber - 2];
			formValues[`${legPrefix}StationTank`] = {
				stationId: leg.stationId,
				tankNumber: leg.tankNumber,
				productType: leg.productType,
			};
		});
		formValues.extraLegCount = Math.max(legs.length - 1, 0);

		//- Dates
		formValues.startDate = deliveryWindowStartDateTime;

		if (!allDayWindow) {
			formValues.startTime = deliveryWindowStartDateTime;
			formValues.endDate = deliveryWindowEndDateTime;
			formValues.endTime = deliveryWindowEndDateTime;
		}

		return formValues;
	};

	addInput = (arrayName) => {
		this.setState({ [arrayName]: this.state[arrayName].concat(['']) });
	};

	removeInput = (arrayName, index) => {
		const inputs = this.state[arrayName].slice();
		inputs.splice(index, 1);
		this.setState({ [arrayName]: inputs });
	};

	//- go through each picked supplier nad make sure this carrier is cleared
	validateAndUpdate = ({ newState }) => {
		const { showErrors, terminals, carriers, supplierMap } = this.props;
		const mergedState = _.merge({}, this.state, newState);
		const {
			primaryProductType,
			carrierId,
			firstTerminalTerminalId,
			firstTerminalSupplierId,
			secondTerminalTerminalId,
			secondTerminalSupplierId,
			assignmentStats,
			secondTerminalUseLoadingAssignments,
			//firstTerminalBackups, secondTerminalBackups //XXX not checking on backups
		} = mergedState;

		if (!terminals || !carrierId || !carriers || !supplierMap) {
			this.setState(newState);
			return;
		}

		const errors = [];
		if (firstTerminalTerminalId && firstTerminalSupplierId) {
			const terminal = _.find(terminals, { terminalId: firstTerminalTerminalId });
			const sp = _.find(terminal.supplierProducts, { supplierId: firstTerminalSupplierId });
			if (!sp) {
				return console.error(
					`Missing supplier product : Terminal : ${firstTerminalTerminalId}, ` +
					`supplierId:${firstTerminalSupplierId}.`
				);
			}

			if (!sp.clearedCarrierMap || !sp.clearedCarrierMap[carrierId]) {
				const carrier = _.find(carriers, { carrierId });
				const supplier = supplierMap[firstTerminalSupplierId];
				errors.push(
					`${carrier.name} is not cleared to pick up ${primaryProductType} fuel ` +
					` from ${terminal.name} using supplier ${supplier.name}`
				);
			}
		}

		if (secondTerminalTerminalId && secondTerminalSupplierId) {
			const terminal = _.find(terminals, { terminalId: secondTerminalTerminalId });
			if (!terminal) {
				return console.error(`Missing terminal : ${secondTerminalTerminalId}`);
			}
			const sp = _.find(terminal.supplierProducts, { supplierId: secondTerminalSupplierId });
			if (!sp) {
				return console.error(
					`Missing supplier product : Terminal : ${secondTerminalTerminalId}, ` +
					`supplierId:${secondTerminalSupplierId}.`
				);
			}

			if (!sp.clearedCarrierMap || !sp.clearedCarrierMap[carrierId]) {
				const carrier = _.find(carriers, { carrierId });
				const supplier = supplierMap[secondTerminalSupplierId];
				errors.push(
					`${carrier.name} is not cleared to pick up etoh fuel ` +
					` from ${terminal.name} using supplier ${supplier.name}`
				);
			}
		}

		//- Check to see if we have loading numbers available
		if (secondTerminalUseLoadingAssignments) {
			if (_.isEmpty(assignmentStats)) {
				errors.push('No loading numbers in the system');
			} else {
				let hasAvailable = false;
				assignmentStats.forEach((stat) => {
					const { terminalId, supplierId, available } = stat;

					if (
						terminalId === secondTerminalTerminalId &&
						supplierId === secondTerminalSupplierId &&
						available
					) {
						hasAvailable = true;
					}
				});

				if (!hasAvailable) {
					errors.push('WARNING : No loading numbers available for this terminal / supplier');
				}
			}
		}

		if (errors.length) {
			showErrors(errors);
		}

		this.setState(newState);
	};

	getValue = (path, defaultValue) => {
		const _d = _.isUndefined(defaultValue) ? '' : defaultValue;
		return _.get(this.state, path, _d);
	};
	getError = (path) => {
		return _.get(this.state.formFieldError, path);
	};

	render() {
		const {
			classes,
			stationDelivery,
			stations,
			carriers,
			terminals,
			resourceMap,
			activateDelivery,
			cancelDelivery,
			uncancelDelivery,
			updateDelivery,
			history,
			showErrors,
			stationMap,
		} = this.props;

		const {
			isUpdate,
			stationDeliveryId,
			//primaryStationTank,
			attachmentResourceIds,
			extraLegCount,
			isConfirmDialogOpen,
			isUpdatingDialogOpen,
			isCancelDialogOpen,
			isUncancelDialogOpen,
			updatingStatus,
			estimate,
			isFetchingEstimate,
			confirmingStationDelivery,
			confirmingJobResult,
			allDayWindow,
			assignmentStats,
			primaryStationTank,
			secondTerminalTerminalId,
			carrierId,
		} = this.state;

		const extraLegPrefixes = this.getExtraLegPrefixes();

		if (!terminals.length || !carriers.length || !stations.length) {
			return <div>Fetching information...</div>;
		}

		if (!_.isArray(assignmentStats)) {
			return <div>Fetching loading number info..</div>;
		}

		if (isUpdate && !stationDelivery) {
			return <div>Fetching stationDelivery information...</div>;
		}
		const verb = isUpdate ? 'Update' : 'Add';
		const isCancelled = Boolean(isUpdate && stationDelivery && stationDelivery.isCancelled);
		const isLocked = this.isDeliveryLocked();
		const canEditLocked = this.canEditInvoicedDeliveries();
		//- Locked and no override permission: everything except the internal note is read-only
		const isReadOnly = isLocked && !canEditLocked;

		// This is strange but for an external logistics may be assigned a station
		// they are not the primary carrier for and still need to see the station
		// in the list JUST for this delivery so we splice it in
		let stationsForDropDown = stations;
		if (_.keys(stationMap).length !== stations.length) {
			stationsForDropDown = _.values(stationMap);
		}

		const resources = [];
		attachmentResourceIds.forEach((resourceId, i) => {
			const resource = resourceMap[resourceId];
			resources.push(resource);
			resource.preview = (
				<div style={{ width: 75, height: 75, backgroundColor: '#ddd' }}>
					<span style={{ fontSize: 14 }}>No preview</span>
					<br />
					<a href={resource.url} target="_blank" rel="noreferrer noopener">
						Download
					</a>
				</div>
			);

			if (!resource.contentType) {
				return;
			}

			/** @namespace resource.contentType **/
			if (resource.contentType.match(/(png|jpg|jpeg|bmp)/)) {
				resource.preview = (
					<img
						alt={`Attachment ${i + 1}`}
						style={{ maxHeight: 175, maxWidth: 175 }}
						src={resource.url}
					/>
				);
			} else if (resource.contentType.match(/(pdf)/)) {
				resource.preview = (
					<object
						data={resource.url}
						style={{ maxHeight: 175, maxWidth: 175 }}
						type="application/pdf"
						width="175"
						height="175"
					>
						<a href={resource.url}>attachment.pdf</a>
					</object>
				);
			}
		});

		const { primaryAmountHelper, extraAmountHelpers } = this.calculateAmounts();

		let isAllTech = secondTerminalTerminalId === ALL_TECH_TERMINAL_ID;
		let primaryDeliveryNotes = '';
		let primaryDeliveryWarnings = '';
		let carrierWarnings = '';
		const stationLookup = _.keyBy(stations, 'stationId');
		if (primaryStationTank) {
			const currentStation = stationLookup[primaryStationTank.stationId];
			if (currentStation) {
				primaryDeliveryNotes = currentStation.deliveryNotes;
				if (currentStation.creditHoldStartDate) {
					primaryDeliveryWarnings = `This station is currently on a CREDIT HOLD. Any new deliveries should be cleared
					with accounting.`;
				}

				if (carrierId && carrierId !== currentStation.primaryCarrierId) {
					carrierWarnings = `This carrier is not listed as the primary carrier on the station.`;
				}
			}
		}

		//- Notes / warnings per extra stop
		const extraLegInfo = {};
		extraLegPrefixes.forEach((legPrefix, i) => {
			const info = { deliveryNotes: '', deliveryWarnings: '' };
			extraLegInfo[legPrefix] = info;

			const stationTank = this.state[`${legPrefix}StationTank`];
			if (!stationTank) {
				return;
			}
			const currentStation = stationLookup[stationTank.stationId];
			if (!currentStation) {
				return;
			}
			info.deliveryNotes = currentStation.deliveryNotes;
			if (currentStation.creditHoldStartDate) {
				info.deliveryWarnings = `This station is currently on a CREDIT HOLD. Any new deliveries should be cleared
				with accounting.`;
			}
			if (carrierId && carrierId !== currentStation.primaryCarrierId) {
				carrierWarnings += `  This carrier is not listed as the primary carrier on station #${i + 2}.`;
			}
		});

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Typography variant="h5" align={'left'} gutterBottom>
								{verb} Delivery {isUpdate && stationDeliveryId}
							</Typography>
						</Grid>
					</Grid>

					{isCancelled && (
						<div className={classes.cancelledAlert}>
							This delivery is CANCELLED
							{stationDelivery.cancellationReason
								? ` : ${stationDelivery.cancellationReason}`
								: ''}
						</div>
					)}
					{isLocked && <LockedDeliveryAlert canEditLocked={canEditLocked} />}
					<fieldset
						disabled={isReadOnly}
						className={`${classes.formFieldset}${isReadOnly ? ` ${classes.lockedForm}` : ''}`}
					>
						<Paper className={classes.paper}>
							<div className={classes.sectionHeader}>Station #1</div>

							<StationTankSelect
								path={'primaryStationTank'}
								label={'Station'}
								controller={this}
								stations={stationsForDropDown}
								onChange={this.onStationTankChange}
								value={this.getValue('primaryStationTank')}
								error={this.getError('primaryStationTank')}
								doNotShowInactive={true}
							/>

							{primaryDeliveryWarnings && (
								<Note type="warn">
									<WarningIcon className={classes.errorIcon} />
									{primaryDeliveryWarnings}
								</Note>
							)}

							{primaryDeliveryNotes && (
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Note>
											<strong style={{ fontSize: '13px' }}>Delivery Note</strong>{' '}
											<pre style={{ margin: 0, paddingLeft: 10 }}>{primaryDeliveryNotes}</pre>
										</Note>
									</Grid>
								</Grid>
							)}

							<Grid container spacing={2}>
								<FMPTextField
									name={'primaryDeliveryAmount'}
									label={'Fuel Delivery Amount'}
									controller={this}
									helpText={primaryAmountHelper}
									md={3}
									contained={false}
								/>
							</Grid>

							<DeliveryEstimate isFetching={isFetchingEstimate} deliveryEstimate={estimate} />

							{extraLegPrefixes.map((legPrefix, i) => {
								const stationNumberLabel = i + 2;
								const legInfo = extraLegInfo[legPrefix] || {};
								return (
									<div key={legPrefix}>
										<div className={classes.sectionHeader}>
										Station #{stationNumberLabel}
											<Button
												size="small"
												style={{ marginLeft: 15 }}
												onClick={() => this.handleRemoveSplitLeg(i)}
											>
												<DeleteIcon style={{ width: 18, height: 18 }} /> Remove
											</Button>
										</div>
										<StationTankSelect
											path={`${legPrefix}StationTank`}
											label={`Station #${stationNumberLabel}`}
											controller={this}
											stations={stationsForDropDown}
											onChange={this.onStationTankChange}
											value={this.getValue(`${legPrefix}StationTank`)}
											error={this.getError(`${legPrefix}StationTank`)}
										/>

										{legInfo.deliveryWarnings && (
											<Note type="warn">
												<WarningIcon className={classes.errorIcon} />
												{legInfo.deliveryWarnings}
											</Note>
										)}

										{legInfo.deliveryNotes && (
											<Grid container spacing={2}>
												<Grid item xs={12}>
													<Note>
														<strong style={{ fontSize: '13px' }}>Delivery Note</strong>{' '}
														<pre style={{ margin: 0, paddingLeft: 10 }}>{legInfo.deliveryNotes}</pre>
													</Note>
												</Grid>
											</Grid>
										)}

										<FMPTextField
											name={`${legPrefix}DeliveryAmount`}
											label={'Fuel Delivery Amount'}
											controller={this}
											helpText={extraAmountHelpers[legPrefix]}
										/>

										<DeliveryEstimate
											isFetching={this.state[`isFetching${upperFirst(legPrefix)}Estimate`]}
											deliveryEstimate={this.state[`${legPrefix}Estimate`]}
										/>
									</div>
								);
							})}

							{extraLegCount < EXTRA_LEG_PREFIXES.length && (
								<Button
									variant="contained"
									className={classes.addInputButton}
									onClick={this.handleAddSplitLeg}
								>
									{extraLegCount === 0 ? 'SPLIT LOAD' : 'ADD ANOTHER STATION'}
								</Button>
							)}

							{extraLegCount === 1 && (
								<Button
									variant="contained"
									className={classes.addInputButton}
									style={{ marginLeft: 10 }}
									onClick={() => this.handleRemoveSplitLeg(0)}
								>
								DON&apos;T SPLIT LOAD
								</Button>
							)}

							<Grid spacing={2} container>
								<Grid item xs={12} md={3}>
									<div className={classes.sectionHeader}>Delivery Window</div>
								</Grid>
								<Grid item xs={12} md={3}>
									<div className={classes.allDayContainer}>
										<FormControlLabel
											control={
												<Switch
													checked={allDayWindow || false}
													onChange={(e) => this.onChange(e, 'allDayWindow')}
												/>
											}
											label="Use all day window"
										/>
									</div>
								</Grid>
							</Grid>

							<Grid spacing={2} container>
								<Grid item xs={12} md={3}>
									<FMPDatePicker
										label="State Date"
										path={'startDate'}
										onChange={this.onDateTimeChange}
										value={this.getValue('startDate')}
										error={this.getError('startDate')}
									/>
								</Grid>
								<Grid item xs={12} md={3}>
									<FMPTimePicker
										label="Start Time"
										path={'startTime'}
										onChange={this.onDateTimeChange}
										value={this.getValue('startTime')}
										error={this.getError('startTime')}
										disabled={allDayWindow}
									/>
								</Grid>
							</Grid>

							<Grid spacing={2} container style={{ marginTop: -10 }}>
								<Grid item xs={12} md={3}>
									<FMPDatePicker
										label="End Date"
										path={'endDate'}
										onChange={this.onDateTimeChange}
										value={this.getValue('endDate')}
										error={this.getError('endDate')}
										disabled={allDayWindow}
									/>
								</Grid>
								<Grid item xs={12} md={3}>
									<FMPTimePicker
										label="End Time"
										path={'endTime'}
										onChange={this.onDateTimeChange}
										value={this.getValue('endTime')}
										error={this.getError('endTime')}
										disabled={allDayWindow}
									/>
								</Grid>
							</Grid>

							<div className={classes.sectionHeader}>Carrier</div>
							<CarrierSelect
								name={'carrierId'}
								label={'Carrier'}
								controller={this}
								carriers={carriers}
								disabled={isReadOnly}
							/>

							{carrierWarnings && (
								<Note type="warn">
									<WarningIcon className={classes.warningIcon} />
									{carrierWarnings}
								</Note>
							)}
						</Paper>

						{this.renderProductSection()}

						<Paper className={classes.paper}>
							<FMPRegionHeader isTop={true} label="Email Info" />
							<FMPTextField name={'note'} label={'Notes'} controller={this} multiline={3} />

							{resources.map((resource, i) => {
								return (
									<Grid key={i} container spacing={3} style={{ marginTop: 10 }}>
										<Grid item xs={11} md={4}>
											<div>Attachment #{i + 1}</div>
											{resource.preview}
										</Grid>
										<Grid item xs={11} md={4}>
											{resource.contentType}
										</Grid>
										<Grid item xs={1}>
											<IconButton
												style={{ transform: 'translateY(50%)' }}
												color="primary"
												className={classes.button}
												onClick={() => this.removeInput('attachmentResourceIds', i)}
											>
												<DeleteIcon />
											</IconButton>
										</Grid>
									</Grid>
								);
							})}

							<Grid container spacing={3}>
								<Grid item xs={12} md={6}>
									<ResourceUploader
										name={'attachmentResourceIds'}
										label={'Add attachment'}
										afterUpload={this.handleAttachmentUpload}
									/>
								</Grid>
							</Grid>

							{isAllTech && (
								<div className={classes.allTechAlert}>
								NOTICE : This delivery is using the <strong>All Tech Blend Formula</strong> and thus
								contains out of the ordinary pickup amounts of unleaded and ethanol fuel. However,
								the final blend amount will be standard E85 fuel.
								</div>
							)}
						</Paper>
					</fieldset>

					<Paper className={classes.paper}>
						<FMPRegionHeader isTop={true} label="Internal" />
						<FMPTextField
							name={'internalNote'}
							label={'Internal Notes For This Delivery (only viewable in FMP)'}
							controller={this}
							multiline={3}
						/>

						{!isUpdate && (
							<div style={{ textAlign: 'right' }}>
								<Button
									variant="contained"
									color="primary"
									className={classes.submitButton}
									onClick={this.handleSave}
								>
									Confirm {verb} Delivery
								</Button>
							</div>
						)}

						{isUpdate && (
							<div style={{ paddingTop: 10, textAlign: 'right' }}>
								{!isReadOnly && (
									<Fragment>
										{isCancelled ? (
											<Button
												variant="contained"
												className={classes.addInputButton}
												onClick={this.handleUncancel}
											>
												Uncancel Delivery
											</Button>
										) : (
											<Button
												variant="contained"
												className={classes.addInputButton}
												onClick={this.handleCancel}
											>
												Cancel Delivery
											</Button>
										)}

										<span style={{ paddingLeft: 10 }} />
									</Fragment>
								)}

								<Button
									variant="contained"
									//color="primary"
									className={classes.addInputButton}
									onClick={() => {
										this.handleSave({ sendAlert: false });
									}}
								>
									{isReadOnly ? 'Update Internal Note' : 'Update : No Change Request'}
								</Button>

								{!isReadOnly && (
									<Fragment>
										<span style={{ paddingLeft: 10 }} />

										<Button
											variant="contained"
											color="primary"
											className={classes.submitButton}
											onClick={() => {
												this.handleSave({ sendAlert: true });
											}}
										>
											Update : Send Change Request
										</Button>
									</Fragment>
								)}
							</div>
						)}

						<ConfirmDeliveryDialog
							open={isConfirmDialogOpen}
							onClose={this.closeConfirmDialog}
							activateDelivery={activateDelivery}
							updateDelivery={updateDelivery}
							stationDelivery={confirmingStationDelivery}
							jobResult={confirmingJobResult}
							showErrors={showErrors}
						/>

						<UpdatingDeliveryDialog
							open={isUpdatingDialogOpen}
							onClose={this.closeUpdatingDialog}
							status={updatingStatus}
							history={history}
						/>

						{isUpdate && (
							<ConfirmCancelDeliveryDialog
								open={isCancelDialogOpen}
								onClose={this.closeCancelDialog}
								stationDelivery={stationDelivery}
								cancelDelivery={cancelDelivery}
								showErrors={showErrors}
							/>
						)}

						{isUpdate && (
							<ConfirmUncancelDeliveryDialog
								open={isUncancelDialogOpen}
								onClose={this.closeUncancelDialog}
								stationDelivery={stationDelivery}
								uncancelDelivery={uncancelDelivery}
								showErrors={showErrors}
							/>
						)}

						{isUpdate && (
							<ConfirmLockedDeliveryEditDialog
								open={this.state.isLockedEditDialogOpen}
								onClose={() => this.setState({ isLockedEditDialogOpen: false })}
								onConfirm={(reason) => {
									this.setState({ isLockedEditDialogOpen: false });
									this.handleSave({
										sendAlert: this.state.pendingLockSendAlert,
										lockOverrideReason: reason,
									});
								}}
							/>
						)}
					</Paper>
				</Grid>
			</Grid>
		);
	}

	calculateAmounts = () => {
		const { primaryStationTank, secondTerminalTerminalId, fuelPickupType } = this.state;

		const primaryDeliveryAmount = _.parseInt(this.state.primaryDeliveryAmount);
		const extraLegAmounts = this.getExtraLegPrefixes().map((legPrefix) => ({
			legPrefix,
			amount: _.parseInt(this.state[`${legPrefix}DeliveryAmount`]),
		}));

		let isAllTech = secondTerminalTerminalId === ALL_TECH_TERMINAL_ID;
		let firstTerminalAmount = '????';
		let secondTerminalAmount = '????';
		let primaryAmountHelper = '';
		const extraAmountHelpers = {};

		const { productType } = primaryStationTank;

		if (!productType || !primaryDeliveryAmount || primaryDeliveryAmount < 99) {
			return {
				firstTerminalAmount,
				secondTerminalAmount,
				primaryAmountHelper,
				extraAmountHelpers,
			};
		}

		if (productType === 'e85' && fuelPickupType === 'dualFuelPickup') {
			const blend = (amount) => (isAllTech ? calcAllTechBlend(amount) : calcE85Blend(amount));

			const primaryCalc = blend(primaryDeliveryAmount);
			primaryAmountHelper = `Blend : ${primaryCalc.calculatedEtoh} (etoh) / ${primaryCalc.calculatedCarbob} (unl)`;
			let totalDeliveryAmount = primaryDeliveryAmount;
			firstTerminalAmount = primaryCalc.calculatedCarbob;
			secondTerminalAmount = primaryCalc.calculatedEtoh;

			//- Each extra stop adds its own blend
			let hasExtraAmounts = false;
			extraLegAmounts.forEach(({ legPrefix, amount }) => {
				if (!amount) {
					return;
				}
				hasExtraAmounts = true;
				const calc = blend(amount);

				extraAmountHelpers[legPrefix] =
					`Blend : ${calc.calculatedEtoh} (etoh) / ${calc.calculatedCarbob} (unl)`;
				totalDeliveryAmount += amount;

				firstTerminalAmount += calc.calculatedCarbob;
				secondTerminalAmount += calc.calculatedEtoh;
			});

			if (hasExtraAmounts) {
				primaryAmountHelper += ` -- Total : ${totalDeliveryAmount}`;
				extraLegAmounts.forEach(({ legPrefix, amount }) => {
					if (!amount) {
						return;
					}
					extraAmountHelpers[legPrefix] += ` -- Total : ${totalDeliveryAmount}`;
				});
			}
		} else {
			firstTerminalAmount = primaryDeliveryAmount;
			extraLegAmounts.forEach(({ amount }) => {
				if (amount) {
					firstTerminalAmount += amount;
				}
			});
		}

		return {
			firstTerminalAmount,
			secondTerminalAmount,
			primaryAmountHelper,
			extraAmountHelpers,
		};
	};

	renderProductSection = () => {
		const { classes } = this.props;

		const { primaryStationTank } = this.state;

		const { productType } = primaryStationTank;

		return (
			<Paper className={classes.paper}>
				<FMPRegionHeader isTop={true} label="Fuel Info" />

				{!productType && (
					<div className={classes.pickFuelType}>
						Please first pick a station and tank and then the correct fuel fields will be available.
					</div>
				)}

				{productType && this.renderProductFields({ productType })}
			</Paper>
		);
	};

	renderProductFields = ({ productType }) => {
		const { classes } = this.props;

		const { fuelPickupType } = this.state;

		const e85BlendOptions = [
			{ display: 'Pickup E85 (single fuel pickup)', value: 'singleFuelPickup' },
			{ display: 'Pickup UNL & ETOH (dual fuel pickup)', value: 'dualFuelPickup' },
		];

		if (productType === 'e85') {
			return (
				<Fragment>
					<FMPSelect
						label="E85 fuel pickup option"
						options={e85BlendOptions}
						path={'fuelPickupType'}
						onChange={this.onPureChange}
						value={this.getValue('fuelPickupType')}
						error={this.getError('fuelPickupType')}
						md={6}
					/>

					{!fuelPickupType && (
						<div className={classes.pickFuelType}>Pick a pickup blend type for the E85 fuel.</div>
					)}

					{fuelPickupType === 'singleFuelPickup' && this.renderSingleTerminalPickup('e85')}

					{fuelPickupType === 'dualFuelPickup' &&
						this.renderDualTerminalPickup({
							firstProductType: 'unl',
							secondProductType: 'etoh',
						})}
				</Fragment>
			);
		} else {
			return this.renderSingleTerminalPickup(productType);
		}
	};

	//- The extra stops the pickers need: station display info per stop
	getSplitLegsForPicker = () => {
		const { stationMap } = this.props;
		return this.getExtraLegPrefixes().map((legPrefix, i) => {
			const stationTank = this.state[`${legPrefix}StationTank`] || {};
			return {
				legNumber: i + 2,
				stationId: stationTank.stationId,
				stationNumber: _.get(stationMap, [stationTank.stationId, 'number'], ''),
				stationName: _.get(stationMap, [stationTank.stationId, 'name'], ''),
			};
		});
	};

	renderSingleTerminalPickup = (productType) => {
		const { classes, terminals, supplierMap, stationMap } = this.props;

		const {
			stationDeliveryId,
			primaryStationTank,
			firstTerminalTerminalId,
			firstTerminalSupplierId,
			firstTerminalLoadingNumber,
			firstTerminalSecondaryLoadingNumber,
			firstTerminalTertiaryLoadingNumber,
			firstTerminalQuaternaryLoadingNumber,
			firstTerminalAccountNumber,
			//firstTerminalProductType,
			firstTerminalEmailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber,
			firstTerminalRequireLoadingNumberPerStationOnSplitLoads,
			firstTerminalCarrierNeedsToProvidePickupWindow,
			firstTerminalBackups,

			formFieldError,
		} = this.state;

		const primaryStationId = _.get(primaryStationTank, 'stationId');
		const stationNumber = _.get(stationMap, [primaryStationId, 'number'], '');
		const stationName = _.get(stationMap, [primaryStationId, 'name'], '');
		const splitLegs = this.getSplitLegsForPicker();

		const { firstTerminalAmount } = this.calculateAmounts();

		const fuelDisplay = valueToDisplay(productType);
		return (
			<Fragment>
				<div className={classes.sectionHeader}>{fuelDisplay} Pickup</div>
				<div className={classes.terminalSection}>
					<div className={classes.pickup}>
						Pickup <strong>{firstTerminalAmount}</strong> {fuelDisplay} gallons from:
					</div>
					<TerminalSupplierPicker
						label={`Terminal for ${fuelDisplay}`}
						error={formFieldError['firstTerminalTerminalId']}
						terminalId={firstTerminalTerminalId}
						supplierId={firstTerminalSupplierId}
						loadingNumber={firstTerminalLoadingNumber}
						secondaryLoadingNumber={firstTerminalSecondaryLoadingNumber}
						tertiaryLoadingNumber={firstTerminalTertiaryLoadingNumber}
						quaternaryLoadingNumber={firstTerminalQuaternaryLoadingNumber}
						accountNumber={firstTerminalAccountNumber}
						emailCopyOfDeliveryRequest={firstTerminalEmailCopyOfDeliveryRequest}
						useStationDeliveryIdAsLoadingNumber={firstTerminalUseStationDeliveryIdAsLoadingNumber}
						requireLoadingNumberPerStationOnSplitLoads={
							firstTerminalRequireLoadingNumberPerStationOnSplitLoads
						}
						carrierNeedsToProvidePickupWindow={firstTerminalCarrierNeedsToProvidePickupWindow}
						backups={firstTerminalBackups}
						onChange={this.onFirstTerminalChange}
						terminals={terminals}
						supplierMap={supplierMap}
						productTypes={[productType]}
						stationDeliveryId={stationDeliveryId}
						useAssignedLoadingNumbers={this.state.firstTerminalUseAssignedLoadingNumbers}
						stationId={primaryStationId}
						lookupAssignedLoadingNumbers={this.props.lookupAssignedLoadingNumbers}
						isSplitLoad={splitLegs.length > 0}
						splitLegs={splitLegs}
						stationNumber={stationNumber}
						stationName={stationName}
					/>
				</div>
			</Fragment>
		);
	};

	renderDualTerminalPickup = ({ firstProductType, secondProductType }) => {
		const { classes, terminals, supplierMap, stationMap } = this.props;

		const {
			stationDeliveryId,
			primaryStationTank,
			firstTerminalTerminalId,
			firstTerminalSupplierId,
			firstTerminalLoadingNumber,
			firstTerminalSecondaryLoadingNumber,
			firstTerminalTertiaryLoadingNumber,
			firstTerminalQuaternaryLoadingNumber,
			firstTerminalAccountNumber,
			firstTerminalProductType,
			firstTerminalEmailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber,
			firstTerminalRequireLoadingNumberPerStationOnSplitLoads,
			firstTerminalCarrierNeedsToProvidePickupWindow,
			firstTerminalBackups,

			secondTerminalTerminalId,
			secondTerminalSupplierId,
			secondTerminalLoadingNumber,
			secondTerminalSecondaryLoadingNumber,
			secondTerminalTertiaryLoadingNumber,
			secondTerminalQuaternaryLoadingNumber,
			secondTerminalAccountNumber,
			secondTerminalEmailCopyOfDeliveryRequest,
			secondTerminalUseStationDeliveryIdAsLoadingNumber,
			secondTerminalRequireLoadingNumberPerStationOnSplitLoads,
			secondTerminalUseLoadingAssignments,
			secondTerminalCarrierNeedsToProvidePickupWindow,
			secondTerminalBackups,
			formFieldError,
		} = this.state;

		const primaryStationId = _.get(primaryStationTank, 'stationId');
		const stationNumber = _.get(stationMap, [primaryStationId, 'number'], '');
		const stationName = _.get(stationMap, [primaryStationId, 'name'], '');
		const splitLegs = this.getSplitLegsForPicker();
		const isSplitLoad = splitLegs.length > 0;

		const { firstTerminalAmount, secondTerminalAmount } = this.calculateAmounts();

		const firstFuelDisplay = valueToDisplay(firstProductType);
		const secondFuelDisplay = valueToDisplay(secondProductType);

		return (
			<Fragment>
				<div className={classes.sectionHeader}>{firstFuelDisplay} Pickup</div>
				<div className={classes.terminalSection}>
					<div className={classes.pickup}>
						Pickup <strong>{firstTerminalAmount}</strong> unleaded gallons from:
					</div>

					<FMPProductSelect
						productTypes={['unl', 'midunl', 'preunl']}
						label="Select unleaded fuel type"
						path={'firstTerminalProductType'}
						onChange={this.onUnleadedProductTypeChange}
						value={this.getValue('firstTerminalProductType')}
						error={this.getError('firstTerminalProductType')}
						md={6}
					/>

					<TerminalSupplierPicker
						label={`Terminal for ${firstFuelDisplay}`}
						error={formFieldError['firstTerminalTerminalId']}
						terminalId={firstTerminalTerminalId}
						supplierId={firstTerminalSupplierId}
						loadingNumber={firstTerminalLoadingNumber}
						secondaryLoadingNumber={firstTerminalSecondaryLoadingNumber}
						tertiaryLoadingNumber={firstTerminalTertiaryLoadingNumber}
						quaternaryLoadingNumber={firstTerminalQuaternaryLoadingNumber}
						accountNumber={firstTerminalAccountNumber}
						productTypes={[firstTerminalProductType || 'unl']}
						emailCopyOfDeliveryRequest={firstTerminalEmailCopyOfDeliveryRequest}
						useStationDeliveryIdAsLoadingNumber={firstTerminalUseStationDeliveryIdAsLoadingNumber}
						requireLoadingNumberPerStationOnSplitLoads={
							firstTerminalRequireLoadingNumberPerStationOnSplitLoads
						}
						carrierNeedsToProvidePickupWindow={firstTerminalCarrierNeedsToProvidePickupWindow}
						backups={firstTerminalBackups}
						onChange={this.onFirstTerminalChange}
						terminals={terminals}
						supplierMap={supplierMap}
						stationDeliveryId={stationDeliveryId}
						useAssignedLoadingNumbers={this.state.firstTerminalUseAssignedLoadingNumbers}
						stationId={primaryStationId}
						lookupAssignedLoadingNumbers={this.props.lookupAssignedLoadingNumbers}
						isSplitLoad={isSplitLoad}
						splitLegs={splitLegs}
						stationNumber={stationNumber}
						stationName={stationName}
					/>
				</div>

				<div className={classes.sectionHeader}>{secondFuelDisplay} Pickup</div>
				<div className={classes.terminalSection}>
					<div className={classes.pickup}>
						Pickup <strong>{secondTerminalAmount}</strong> ethanol gallons from:
					</div>
					<TerminalSupplierPicker
						label={`Terminal for ${secondFuelDisplay}`}
						error={formFieldError['secondTerminalTerminalId']}
						terminalId={secondTerminalTerminalId}
						supplierId={secondTerminalSupplierId}
						loadingNumber={secondTerminalLoadingNumber}
						secondaryLoadingNumber={secondTerminalSecondaryLoadingNumber}
						tertiaryLoadingNumber={secondTerminalTertiaryLoadingNumber}
						quaternaryLoadingNumber={secondTerminalQuaternaryLoadingNumber}
						accountNumber={secondTerminalAccountNumber}
						emailCopyOfDeliveryRequest={secondTerminalEmailCopyOfDeliveryRequest}
						useStationDeliveryIdAsLoadingNumber={secondTerminalUseStationDeliveryIdAsLoadingNumber}
						requireLoadingNumberPerStationOnSplitLoads={
							secondTerminalRequireLoadingNumberPerStationOnSplitLoads
						}
						useLoadingAssignments={secondTerminalUseLoadingAssignments}
						carrierNeedsToProvidePickupWindow={secondTerminalCarrierNeedsToProvidePickupWindow}
						backups={secondTerminalBackups}
						terminals={terminals}
						supplierMap={supplierMap}
						productTypes={['etoh']}
						onChange={this.onSecondTerminalChange}
						stationDeliveryId={stationDeliveryId}
						useAssignedLoadingNumbers={this.state.secondTerminalUseAssignedLoadingNumbers}
						stationId={primaryStationId}
						lookupAssignedLoadingNumbers={this.props.lookupAssignedLoadingNumbers}
						isSplitLoad={isSplitLoad}
						splitLegs={splitLegs}
						stationNumber={stationNumber}
						stationName={stationName}
					/>
				</div>
			</Fragment>
		);
	};
}

SaveStationDelivery.propTypes = {
	classes: PropTypes.object.isRequired,
	getDelivery: PropTypes.func.isRequired,
	addDelivery: PropTypes.func.isRequired,
	activateDelivery: PropTypes.func.isRequired,
	updateDelivery: PropTypes.func.isRequired,
	cancelDelivery: PropTypes.func.isRequired,
	uncancelDelivery: PropTypes.func.isRequired,
	getStationDeliveryCarrierEmail: PropTypes.func.isRequired,
	lookupAssignedLoadingNumbers: PropTypes.func,
	estimateDelivery: PropTypes.func.isRequired,

	stationDelivery: PropTypes.object,

	searchStations: PropTypes.func.isRequired,
	stations: PropTypes.array,

	searchCarriers: PropTypes.func.isRequired,
	carriers: PropTypes.array,

	searchTerminals: PropTypes.func.isRequired,
	terminals: PropTypes.array,
	supplierMap: PropTypes.object,
	resourceMap: PropTypes.object,
	stationMap: PropTypes.object,

	getLoadingAssignmentStats: PropTypes.func,
	showErrors: PropTypes.func.isRequired,
	history: PropTypes.object.isRequired,
	authUser: PropTypes.object,
};

export default _.flow([
	withStyles(styles),
	LoadingAssignmentsContainer,
	DeliveryContainer,
	StationsContainer,
	CarriersContainer,
	TerminalsContainer,
	AuthUserContainer,
])(SaveStationDelivery);
